import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  useEffect,
} from "react";
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";
import {
  FORM_TIMING,
  VALIDATION,
  STORAGE,
  FORM_TYPES,
} from "../constants/formConstants";
import { getDraftById } from "../utils/draftStorage";
import { cleanLabel } from "../utils/validationUtils";

const trimStringValues = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === "string") {
    return obj.trim();
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => trimStringValues(item));
  }

  if (typeof obj === "object") {
    const trimmedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        trimmedObj[key] = trimStringValues(obj[key]);
      }
    }
    return trimmedObj;
  }

  return obj;
};

const MultiFormContext = createContext();
const FORM_ACTIONS = {
  SET_FORM_DATA: "SET_FORM_DATA",
  SET_FORM_ERRORS: "SET_FORM_ERRORS",
  SET_FORM_LOADING: "SET_FORM_LOADING",
  SET_SUBMISSION_STATE: "SET_SUBMISSION_STATE",
  CLEAR_FORM_ERRORS: "CLEAR_FORM_ERRORS",
  RESET_FORMS: "RESET_FORMS",
  SET_FORM_VALIDITY: "SET_FORM_VALIDITY",
  HYDRATE_FROM_STORAGE: "HYDRATE_FROM_STORAGE",
  SET_SUB_FORMS: "SET_SUB_FORMS",
  SET_REQUEST: "SET_REQUEST",
  SET_FILLED_FORM: "SET_FILLED_FORM",
  SET_HYDRATION_COMPLETE: "SET_HYDRATION_COMPLETE",
  SET_API_DATA_LOADED: "SET_API_DATA_LOADED",
  SET_INITIALIZATION_COMPLETE: "SET_INITIALIZATION_COMPLETE",
  SET_FORM_NAME: "SET_FORM_NAME",
  SET_WORKFLOW_NAME: "SET_WORKFLOW_NAME",
  SET_ACTIVE_SUB_FORM_TAB: "SET_ACTIVE_SUB_FORM_TAB",
  ADD_ADDITIONAL_DOCUMENT: "ADD_ADDITIONAL_DOCUMENT",
  UPDATE_ADDITIONAL_DOCUMENT: "UPDATE_ADDITIONAL_DOCUMENT",
  DELETE_ADDITIONAL_DOCUMENT: "DELETE_ADDITIONAL_DOCUMENT",
  SET_ADDITIONAL_DOCUMENTS: "SET_ADDITIONAL_DOCUMENTS",
};

const initialState = {
  formSections: {},
  drugPricing: {
    drug_name_brand: "",
    generic_name: "",
    dosage_form: "",
    strength: "",
    manufacturer_name: "",
    country_of_origin: "",
    marketing_authorization_holder: "",
    local_agent_distributor: "",
    proposed_price_local: "",
    currency: VALIDATION.DEFAULT_CURRENCY,
    cif_price: "",
    ex_factory_price: "",
    packaging_details: "",
    contact_email: "",
    phone_number: "",
    remarks_justification: "",
    reference_country_prices: [VALIDATION.REFERENCE_COUNTRY_DEFAULT],
    price_certificate_file_id: null,
    gmp_certificate_file_id: null,
    market_authorization_certificate_file_id: null,
    packaging_samples_file_id: null,
    pricing_history_file_id: null,
  },
  ectdUpload: {
    ectd_file: [],
    sequence_number: "",
    submission_id: "",
    product_name: "",
    review_objective: "",
    submission_type: "",
    biologic_product_type: "",
  },
  originalEctdUpload: null,
  productDetails: {
    proposed_trade_name_in_english: "",
    proposed_trade_name_in_arabic: "",
    inn_scientific_name: "",
    atc_code: "",
    dosage_form: "",
    route_administration: "",
    concentration: "",
    concentration_unit: "",
    label_artwork_en_ar_file: null,
    pil_english_file: null,
    pil_arabic_file: null,
    spc_file: null,
  },
  manufacturersSite: {
    manufacturer_license: "",
    manufacturer_name: "",
    selected_manufacturer: "",
    activity: "",
    active_substance: "",
    manufacturers_site_address: "",
    manufacturing_process_details: "",
    quality_control_information: "",
    contact_person_name: "",
    email: "",
    phone: "",
    gmp_certificate_file_id: null,
  },
  additionalDocuments: [],
  subForms: [],
  request: null,
  filledForm: null,
  formName: "",
  workflowName: "",
  activeSubFormTab: null,
  hasProductDetails: false,
  hasManufacturersSite: false,
  isHydrationComplete: false,
  hasHydratedData: false,
  isApiDataLoaded: false,
  isInitializationComplete: false,
  errors: {
    formSections: {},
    drugPricing: {},
    ectdUpload: {},
    productDetails: {},
    manufacturersSite: {},
  },
  loading: {
    formSections: false,
    drugPricing: false,
    ectdUpload: false,
    productDetails: false,
    manufacturersSite: false,
  },
  validity: {
    formSections: false,
    drugPricing: false,
    ectdUpload: false,
    productDetails: false,
    manufacturersSite: false,
  },
  submissionState: {
    isSubmitting: false,
    success: false,
    error: null,
    data: null,
  },
};

const multiFormReducer = (state, action) => {
  switch (action.type) {
    case FORM_ACTIONS.SET_FORM_DATA:
      const newData = {
        ...state[action.formType],
        ...action.data,
      };

      return {
        ...state,
        [action.formType]: newData,
      };

    case FORM_ACTIONS.SET_FORM_ERRORS:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.formType]: action.errors,
        },
      };

    case FORM_ACTIONS.SET_FORM_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.formType]: action.loading,
        },
      };

    case FORM_ACTIONS.SET_SUBMISSION_STATE:
      return {
        ...state,
        submissionState: {
          ...state.submissionState,
          ...action.state,
        },
      };

    case FORM_ACTIONS.CLEAR_FORM_ERRORS:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.formType]: {},
        },
      };

    case FORM_ACTIONS.SET_FORM_VALIDITY:
      return {
        ...state,
        validity: {
          ...state.validity,
          [action.formType]: action.valid,
        },
      };

    case FORM_ACTIONS.RESET_FORMS:
      return {
        ...initialState,
        formSections: state.formSections,
        additionalDocuments: state.additionalDocuments,
      };

    case FORM_ACTIONS.HYDRATE_FROM_STORAGE:
      const hydratedState = {
        ...state,
        formSections: {
          ...state.formSections,
          ...action.data.formSections,
        },
        drugPricing: {
          ...state.drugPricing,
          ...action.data.drugPricing,
        },
        ectdUpload: {
          ...state.ectdUpload,
          ...action.data.ectdUpload,
        },
        productDetails: {
          ...state.productDetails,
          ...action.data.productDetails,
        },
        manufacturersSite: {
          ...state.manufacturersSite,
          ...action.data.manufacturersSite,
        },
        additionalDocuments:
          action.data.additionalDocuments || state.additionalDocuments || [],
      };

      return hydratedState;

    case FORM_ACTIONS.SET_SUB_FORMS:
      return {
        ...state,
        subForms: action.subForms,
      };

    case FORM_ACTIONS.SET_REQUEST:
      return {
        ...state,
        request: action.request,
      };

    case FORM_ACTIONS.SET_FILLED_FORM:
      return {
        ...state,
        filledForm: action.filledForm,
      };

    case FORM_ACTIONS.SET_HYDRATION_COMPLETE:
      return {
        ...state,
        isHydrationComplete: true,
        hasHydratedData: action.hasData,
      };

    case FORM_ACTIONS.SET_API_DATA_LOADED:
      return {
        ...state,
        isApiDataLoaded: action.loaded,
      };

    case FORM_ACTIONS.SET_INITIALIZATION_COMPLETE:
      return {
        ...state,
        isInitializationComplete: action.complete,
      };

    case FORM_ACTIONS.SET_FORM_NAME:
      return {
        ...state,
        formName: action.formName,
      };

    case FORM_ACTIONS.SET_WORKFLOW_NAME:
      return {
        ...state,
        workflowName: action.workflowName,
      };

    case FORM_ACTIONS.SET_ACTIVE_SUB_FORM_TAB:
      return {
        ...state,
        activeSubFormTab: action.activeSubFormTab,
      };

    case FORM_ACTIONS.ADD_ADDITIONAL_DOCUMENT:
      return {
        ...state,
        additionalDocuments: [action.document, ...state.additionalDocuments],
      };

    case FORM_ACTIONS.UPDATE_ADDITIONAL_DOCUMENT:
      return {
        ...state,
        additionalDocuments: state.additionalDocuments.map((doc) =>
          doc.id === action.documentId ? { ...doc, ...action.updates } : doc
        ),
      };

    case FORM_ACTIONS.DELETE_ADDITIONAL_DOCUMENT:
      return {
        ...state,
        additionalDocuments: state.additionalDocuments.filter(
          (doc) => doc.id !== action.documentId
        ),
      };

    case FORM_ACTIONS.SET_ADDITIONAL_DOCUMENTS:
      return {
        ...state,
        additionalDocuments: action.documents,
      };

    default:
      return state;
  }
};

export const MultiFormProvider = ({
  children,
  requestId,
  stageId: initialStageId,
  workflowVersionId,
  studyName,
  requestProgressId,
  isSendbackMode = false,
  draftId,
}) => {
  const [state, dispatch] = useReducer(multiFormReducer, initialState);
  const formSectionsDataRef = useRef({});
  const modePrefix = isSendbackMode ? "sendBack_" : "newRequest_";
  const oppositeModePrefix = isSendbackMode ? "newRequest_" : "sendBack_";

  // For new requests, use workflowVersionId; for sendback, use requestProgressId or requestId
  const dynamicId = isSendbackMode
    ? requestProgressId || requestId || STORAGE.DRAFT_KEY
    : workflowVersionId || STORAGE.DRAFT_KEY;

  const storageKey = `${STORAGE.MULTI_FORM_PREFIX}${modePrefix}${dynamicId}`;

  const setFormSectionsDataRef = useCallback((getDataFn) => {
    formSectionsDataRef.current = getDataFn;
  }, []);

  const getFormSectionsData = useCallback(() => {
    if (
      formSectionsDataRef.current &&
      typeof formSectionsDataRef.current === "function"
    ) {
      return formSectionsDataRef.current() || {};
    }
    return {};
  }, []);

  const cleanupConflictingStorage = useCallback(() => {
    try {
      // Only clean up keys that could conflict with the current request
      const conflictingKey = `${STORAGE.MULTI_FORM_PREFIX}${oppositeModePrefix}${dynamicId}`;

      if (localStorage.getItem(conflictingKey)) {
        localStorage.removeItem(conflictingKey);
      }
    } catch (error) {
      // Silent error handling
    }
  }, [oppositeModePrefix, dynamicId]);

  const performInitializationIfNeeded = useCallback(
    (subForms = []) => {
      if (
        state.isHydrationComplete &&
        state.isApiDataLoaded &&
        !state.isInitializationComplete
      ) {
        const currentHasData = Object.keys(state.formSections).some(
          (formId) => {
            const formData = state.formSections[formId];
            return (
              formData &&
              Object.values(formData).some((val) => val && val.trim() !== "")
            );
          }
        );

        if (!state.hasHydratedData && !currentHasData && subForms.length > 0) {
          const initialFormData = {};
          subForms.forEach((sf) => {
            initialFormData[sf.id] = {};
          });

          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: "formSections",
            data: initialFormData,
          });
        }

        dispatch({
          type: FORM_ACTIONS.SET_INITIALIZATION_COMPLETE,
          complete: true,
        });
      }
    },
    [
      state.isHydrationComplete,
      state.hasHydratedData,
      state.isApiDataLoaded,
      state.isInitializationComplete,
      state.formSections,
    ]
  );

  const fetchWorkflowDataAndInitialize = useCallback(
    async (workflowVersionId) => {
      if (!workflowVersionId || state.isApiDataLoaded) {
        return;
      }

      try {
        const response = await axiosInstance.get(
          `/api/workflows/endUser/version/${workflowVersionId}`
        );

        const fetchedSubForms = response.data.Stages[0].Form.subforms;
        const formData = response.data.Stages[0].Form;

        dispatch({
          type: FORM_ACTIONS.SET_WORKFLOW_NAME,
          workflowName: response.data.name,
        });

        dispatch({
          type: FORM_ACTIONS.SET_FORM_NAME,
          formName: response.data.Stages[0].Form.name,
        });

        dispatch({
          type: FORM_ACTIONS.SET_SUB_FORMS,
          subForms: fetchedSubForms,
        });

        dispatch({
          type: FORM_ACTIONS.SET_REQUEST,
          request: response.data,
        });

        // set custom form flags
        dispatch({
          type: FORM_ACTIONS.SET_FORM_DATA,
          formType: "hasProductDetails",
          data: formData.has_product_details || false,
        });

        dispatch({
          type: FORM_ACTIONS.SET_FORM_DATA,
          formType: "hasManufacturersSite",
          data: formData.has_manufacturers_site || false,
        });

        dispatch({
          type: FORM_ACTIONS.SET_API_DATA_LOADED,
          loaded: true,
        });

        setTimeout(() => {
          performInitializationIfNeeded(fetchedSubForms);
        }, FORM_TIMING.SYNC_DELAY_MS);
      } catch (error) {
        dispatch({
          type: FORM_ACTIONS.SET_API_DATA_LOADED,
          loaded: false,
        });
      }
    },
    [state.isApiDataLoaded, performInitializationIfNeeded]
  );

  const fetchSendbackDataAndInitialize = useCallback(
    async (requestProgressId) => {
      if (!requestProgressId || state.isApiDataLoaded) {
        return;
      }

      try {
        const response = await axiosInstance.get(
          `/api/requests/enduser/details/${requestProgressId}`
        );
        const data = response.data;

        if (data.filled_form && data.filled_form.value) {
          try {
            const formSectionsData = JSON.parse(data.filled_form.value);
            dispatch({
              type: FORM_ACTIONS.SET_FORM_DATA,
              formType: "formSections",
              data: formSectionsData,
            });
          } catch (error) {
            // Silent error handling
          }
        }

        if (data.drug_pricing_form) {
          const normalizeFileField = (fileData, defaultName) => {
            if (!fileData) {
              return null;
            }

            const file = Array.isArray(fileData) ? fileData[0] : fileData;
            if (!file) {
              return null;
            }

            const normalized = {
              id: file.id,
              name: file.original_name || file.name || defaultName,
              size: Number(file.file_size || file.size || 0),
              type: file.mime_type || file.type || "application/octet-stream",
              original_name: file.original_name || file.name || defaultName,
              file_size: Number(file.file_size || file.size || 0),
              mime_type:
                file.mime_type || file.type || "application/octet-stream",
            };

            return normalized;
          };

          const drugPricingData = {
            id: data.drug_pricing_form.id || null,
            drug_name_brand: data.drug_pricing_form.drug_name_brand || "",
            generic_name: data.drug_pricing_form.generic_name || "",
            dosage_form: data.drug_pricing_form.dosage_form || "",
            strength: data.drug_pricing_form.strength || "",
            manufacturer_name: data.drug_pricing_form.manufacturer_name || "",
            country_of_origin: data.drug_pricing_form.country_of_origin || "",
            marketing_authorization_holder:
              data.drug_pricing_form.marketing_authorization_holder || "",
            local_agent_distributor:
              data.drug_pricing_form.local_agent_distributor || "",
            proposed_price_local:
              data.drug_pricing_form.proposed_price_local || "",
            currency:
              data.drug_pricing_form.currency || VALIDATION.DEFAULT_CURRENCY,
            cif_price: data.drug_pricing_form.cif_price || "",
            ex_factory_price: data.drug_pricing_form.ex_factory_price || "",
            packaging_details: data.drug_pricing_form.packaging_details || "",
            contact_email: data.drug_pricing_form.contact_email || "",
            phone_number: data.drug_pricing_form.phone_number || "",
            remarks_justification:
              data.drug_pricing_form.remarks_justification || "",
            reference_country_prices: data.drug_pricing_form
              .reference_country_prices || [
              VALIDATION.REFERENCE_COUNTRY_DEFAULT,
            ],
            price_certificate_file_id: data.drug_pricing_form
              .priceCertificateFile
              ? [
                  normalizeFileField(
                    data.drug_pricing_form.priceCertificateFile,
                    "price-certificate"
                  ),
                ]
              : [],
            gmp_certificate_file_id: data.drug_pricing_form.gmpCertificateFile
              ? [
                  normalizeFileField(
                    data.drug_pricing_form.gmpCertificateFile,
                    "gmp-certificate"
                  ),
                ]
              : [],
            market_authorization_certificate_file_id: data.drug_pricing_form
              .marketAuthorizationCertificateFile
              ? [
                  normalizeFileField(
                    data.drug_pricing_form.marketAuthorizationCertificateFile,
                    "market-auth-certificate"
                  ),
                ]
              : [],
            packaging_samples_file_id: data.drug_pricing_form
              .packagingSamplesFile
              ? [
                  normalizeFileField(
                    data.drug_pricing_form.packagingSamplesFile,
                    "packaging-samples"
                  ),
                ]
              : [],
            pricing_history_file_id: data.drug_pricing_form.pricingHistoryFile
              ? [
                  normalizeFileField(
                    data.drug_pricing_form.pricingHistoryFile,
                    "pricing-history"
                  ),
                ]
              : [],
          };

          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: "drugPricing",
            data: drugPricingData,
          });
        }

        if (data.ectd_file || data.ectd_version) {
          // Use ectd_version if available (includes sequence_number), otherwise fall back to ectd_file
          const fileData = data.ectd_version?.file || data.ectd_file;

          if (fileData) {
            const normalizedFile = {
              id: fileData.id,
              name: fileData.original_name || fileData.name || "ectd-document",
              size: Number(fileData.file_size || fileData.size || 0),
              type: fileData.mime_type || fileData.type || "application/zip",
              original_name:
                fileData.original_name || fileData.name || "ectd-document",
              file_size: Number(fileData.file_size || fileData.size || 0),
              mime_type:
                fileData.mime_type || fileData.type || "application/zip",
            };

            const ectdData = {
              ectd_file: [normalizedFile],
              sequence_number: data.ectd_version?.sequence_number || "",
              submission_id: data.ectd_version?.submission_id || "",
              product_name: data.ectd_version?.product_name || "",
              submission_type: data.ectd_version?.submission_type || "",
              biologic_product_type:
                data.ectd_version?.biologic_product_type || "",
              clinical: data.ectd_version?.clinical || false,
              cmc_quality: data.ectd_version?.cmc_quality || false,
              non_clinical_toxicology:
                data.ectd_version?.non_clinical_toxicology || false,
              clinical_pharmacology:
                data.ectd_version?.clinical_pharmacology || false,
              biostatistics: data.ectd_version?.biostatistics || false,
              device_engineering:
                data.ectd_version?.device_engineering || false,
              labeling: data.ectd_version?.labeling || false,
              proposed_indication: data.ectd_version?.proposed_indication || [],
              review_pathway: data.ectd_version?.review_pathway || "",
              fast_track: data.ectd_version?.fast_track || false,
              breakthrough_therapy:
                data.ectd_version?.breakthrough_therapy || false,
              rmat: data.ectd_version?.rmat || false,
              accelerated_approval:
                data.ectd_version?.accelerated_approval || false,
              basis_of_clinical_efficacy:
                data.ectd_version?.basis_of_clinical_efficacy || "",
              dosage_form_route: data.ectd_version?.dosage_form_route || "",
            };

            dispatch({
              type: FORM_ACTIONS.SET_FORM_DATA,
              formType: "ectdUpload",
              data: ectdData,
            });

            // Store original eCTD data for comparison in validation
            dispatch({
              type: FORM_ACTIONS.SET_FORM_DATA,
              formType: "originalEctdUpload",
              data: { ...ectdData },
            });
          }
        }

        // load product details if available
        if (data.product_details) {
          const normalizeFileField = (fileData, defaultName) => {
            if (!fileData) {
              return null;
            }

            const file = Array.isArray(fileData) ? fileData[0] : fileData;
            if (!file) {
              return null;
            }

            const normalized = {
              id: file.id,
              name: file.original_name || file.name || defaultName,
              size: Number(file.file_size || file.size || 0),
              type: file.mime_type || file.type || "application/octet-stream",
              original_name: file.original_name || file.name || defaultName,
              file_size: Number(file.file_size || file.size || 0),
              mime_type:
                file.mime_type || file.type || "application/octet-stream",
            };

            return normalized;
          };

          const productDetailsData = {
            id: data.product_details.id || null,
            proposed_trade_name_in_english:
              data.product_details.proposed_trade_name_in_english || "",
            proposed_trade_name_in_arabic:
              data.product_details.proposed_trade_name_in_arabic || "",
            inn_scientific_name: data.product_details.inn_scientific_name || "",
            atc_code: data.product_details.atc_code || "",
            dosage_form: data.product_details.dosage_form || "",
            route_administration:
              data.product_details.route_administration || "",
            concentration: data.product_details.concentration || "",
            concentration_unit: data.product_details.concentration_unit || "",
            label_artwork_en_ar_file: data.product_details.labelArtworkFile
              ? [
                  normalizeFileField(
                    data.product_details.labelArtworkFile,
                    "label-artwork"
                  ),
                ]
              : [],
            pil_english_file: data.product_details.pilEnglishFile
              ? [
                  normalizeFileField(
                    data.product_details.pilEnglishFile,
                    "pil-english"
                  ),
                ]
              : [],
            pil_arabic_file: data.product_details.pilArabicFile
              ? [
                  normalizeFileField(
                    data.product_details.pilArabicFile,
                    "pil-arabic"
                  ),
                ]
              : [],
            spc_file: data.product_details.spcFile
              ? [normalizeFileField(data.product_details.spcFile, "spc")]
              : [],
          };

          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: "productDetails",
            data: productDetailsData,
          });

          // set hasProductDetails flag
          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: "hasProductDetails",
            data: true,
          });
        }

        // load manufacturers site if available
        if (data.manufacturers_site) {
          const normalizeFileField = (fileData, defaultName) => {
            if (!fileData) {
              return null;
            }

            const file = Array.isArray(fileData) ? fileData[0] : fileData;
            if (!file) {
              return null;
            }

            const normalized = {
              id: file.id,
              name: file.original_name || file.name || defaultName,
              size: Number(file.file_size || file.size || 0),
              type: file.mime_type || file.type || "application/octet-stream",
              original_name: file.original_name || file.name || defaultName,
              file_size: Number(file.file_size || file.size || 0),
              mime_type:
                file.mime_type || file.type || "application/octet-stream",
            };

            return normalized;
          };

          const manufacturersSiteData = {
            id: data.manufacturers_site.id || null,
            selected_manufacturer:
              data.manufacturers_site.selected_manufacturer || "",
            manufacturer_name: data.manufacturers_site.manufacturer_name || "",
            manufacturers_site_address:
              data.manufacturers_site.manufacturers_site_address || "",
            manufacturer_license:
              data.manufacturers_site.manufacturer_license || "",
            manufacturing_process_details:
              data.manufacturers_site.manufacturing_process_details || "",
            activity: data.manufacturers_site.activity || "",
            quality_control_information:
              data.manufacturers_site.quality_control_information || "",
            active_substance: data.manufacturers_site.active_substance || "",
            contact_person_name:
              data.manufacturers_site.contact_person_name || "",
            email: data.manufacturers_site.email || "",
            phone: data.manufacturers_site.phone || "",
            activity_substance:
              data.manufacturers_site.activity_substance || "",
            gmp_certificate_file: data.manufacturers_site.gmpCertificateFile
              ? [
                  normalizeFileField(
                    data.manufacturers_site.gmpCertificateFile,
                    "gmp-certificate"
                  ),
                ]
              : [],
          };

          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: "manufacturersSite",
            data: manufacturersSiteData,
          });

          // set hasManufacturersSite flag
          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: "hasManufacturersSite",
            data: true,
          });
        }

        if (data.request) {
          dispatch({
            type: FORM_ACTIONS.SET_REQUEST,
            request: data.request,
          });

          // Get workflow name from workflow version, not form name
          const workflowName =
            data.request?.wf_version?.name ||
            data.request?.WorkflowVersion?.name ||
            data.order?.[0]?.form?.name;

          if (workflowName) {
            dispatch({
              type: FORM_ACTIONS.SET_WORKFLOW_NAME,
              workflowName: workflowName,
            });
          }

          // Get form name from the order or workflow version stages
          const formName =
            data.order?.[0]?.form?.name ||
            data.request?.wf_version?.Stages?.[0]?.Form?.name ||
            data.request?.WorkflowVersion?.Stages?.[0]?.Form?.name;

          if (formName) {
            dispatch({
              type: FORM_ACTIONS.SET_FORM_NAME,
              formName: formName,
            });
          }
        }

        if (data.filled_form) {
          dispatch({
            type: FORM_ACTIONS.SET_FILLED_FORM,
            filledForm: data.filled_form,
          });
        }

        let subformsToSet = null;

        dispatch({
          type: FORM_ACTIONS.SET_API_DATA_LOADED,
          loaded: true,
        });

        dispatch({
          type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
          hasData: true,
        });

        if (subformsToSet && subformsToSet.length > 0) {
          setTimeout(() => {
            performInitializationIfNeeded(subformsToSet);
          }, FORM_TIMING.SYNC_DELAY_MS);
        }

        if (
          !subformsToSet &&
          data.sub_forms_info &&
          Array.isArray(data.sub_forms_info)
        ) {
          const formSectionsData = data.filled_form?.value
            ? JSON.parse(data.filled_form.value)
            : {};
          const subformIds = Object.keys(formSectionsData);

          let matchedSubforms = data.sub_forms_info.filter((subform) =>
            subformIds.includes(subform.id.toString())
          );

          // Use the order array to sort subforms correctly if available
          if (data.order && Array.isArray(data.order)) {
            // Create a map of SubFormId to order value for quick lookup
            const orderMap = {};
            data.order.forEach((orderItem) => {
              orderMap[orderItem.SubFormId] = orderItem.order;
            });

            // Sort subforms based on the order array and add the order property
            matchedSubforms = matchedSubforms
              .map((subform) => ({
                ...subform,
                order:
                  orderMap[subform.id] !== undefined
                    ? orderMap[subform.id] + 1
                    : 999, // +1 to make it 1-based, fallback to 999 for missing order
              }))
              .sort((a, b) => a.order - b.order);
          } else {
            // Fallback to original behavior if no order array is provided
            matchedSubforms = matchedSubforms
              .sort((a, b) => a.id - b.id)
              .map((subform, index) => ({
                ...subform,
                order: index + 1,
              }));
          }

          if (matchedSubforms.length > 0) {
            subformsToSet = matchedSubforms;

            dispatch({
              type: FORM_ACTIONS.SET_SUB_FORMS,
              subForms: matchedSubforms,
            });

            setTimeout(() => {
              performInitializationIfNeeded(matchedSubforms);
            }, FORM_TIMING.SYNC_DELAY_MS);
          }
        } else if (!subformsToSet && data.filled_form?.value) {
          try {
            const formSectionsData = JSON.parse(data.filled_form.value);
            const subformIds = Object.keys(formSectionsData);

            if (subformIds.length > 0) {
              const fallbackSubforms = subformIds.map((id, index) => ({
                id: parseInt(id),
                name: `Form ${index + 1}`,
                json_object: "[]",
                order: index + 1,
              }));

              subformsToSet = fallbackSubforms;

              dispatch({
                type: FORM_ACTIONS.SET_SUB_FORMS,
                subForms: fallbackSubforms,
              });

              setTimeout(() => {
                performInitializationIfNeeded(fallbackSubforms);
              }, FORM_TIMING.SYNC_DELAY_MS);
            }
          } catch (parseError) {
            // Silent error handling
          }
        }
      } catch (error) {
        dispatch({
          type: FORM_ACTIONS.SET_API_DATA_LOADED,
          loaded: false,
        });
        dispatch({
          type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
          hasData: false,
        });
      }
    },
    [state.isApiDataLoaded, performInitializationIfNeeded]
  );

  useEffect(() => {
    // Clean up any conflicting storage data from the opposite mode
    cleanupConflictingStorage();

    if (isSendbackMode) {
      dispatch({
        type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
        hasData: false,
      });
      return;
    }

    try {
      let savedData = null;

      // ONLY load draft data if draftId is explicitly provided in URL
      // This ensures new requests start clean
      if (draftId) {
        const draftData = getDraftById(draftId);
        if (draftData) {
          savedData = JSON.stringify(draftData);
        }
      }

      if (savedData) {
        const parsed = JSON.parse(savedData);

        if (
          parsed.timestamp &&
          Date.now() - parsed.timestamp < FORM_TIMING.STORAGE_EXPIRY_MS
        ) {
          const dataToHydrate = {
            formSections: parsed.formSections || initialState.formSections,
            drugPricing: parsed.drugPricing || initialState.drugPricing,
            ectdUpload: parsed.ectdUpload || initialState.ectdUpload,
            productDetails:
              parsed.productDetails || initialState.productDetails,
            manufacturersSite:
              parsed.manufacturersSite || initialState.manufacturersSite,
            additionalDocuments:
              parsed.additionalDocuments || initialState.additionalDocuments,
          };

          const hasSignificantData = Object.keys(
            dataToHydrate.formSections
          ).some((formId) => {
            const formData = dataToHydrate.formSections[formId];
            return (
              formData &&
              Object.values(formData).some((val) => val && val.trim() !== "")
            );
          });

          dispatch({
            type: FORM_ACTIONS.HYDRATE_FROM_STORAGE,
            data: dataToHydrate,
          });

          dispatch({
            type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
            hasData: hasSignificantData,
          });

          setTimeout(() => {
            if (window.populateJQueryFromReactState) {
              window.populateJQueryFromReactState();
            }

            setTimeout(() => {
              if (window.forceRenderAllForms) {
                window.forceRenderAllForms();
              }
            }, FORM_TIMING.RENDER_DELAY_MS);
          }, FORM_TIMING.HYDRATION_DELAY_MS);
        } else {
          dispatch({
            type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
            hasData: false,
          });
        }
      } else {
        dispatch({
          type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
          hasData: false,
        });
      }
    } catch (error) {
      // Silent error handling
    }
  }, [
    storageKey,
    requestId,
    requestProgressId,
    workflowVersionId,
    isSendbackMode,
    modePrefix,
    dynamicId,
    cleanupConflictingStorage,
    draftId,
  ]);

  const setFormData = useCallback((formType, data) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_DATA,
      formType,
      data,
    });
  }, []);

  const setFormErrors = useCallback((formType, errors) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_ERRORS,
      formType,
      errors,
    });
  }, []);

  const setFormLoading = useCallback((formType, loading) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_LOADING,
      formType,
      loading,
    });
  }, []);

  const setFormValidity = useCallback((formType, valid) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_VALIDITY,
      formType,
      valid,
    });
  }, []);

  const clearFormErrors = useCallback((formType) => {
    dispatch({
      type: FORM_ACTIONS.CLEAR_FORM_ERRORS,
      formType,
    });
  }, []);

  const setSubForms = useCallback((subForms) => {
    dispatch({
      type: FORM_ACTIONS.SET_SUB_FORMS,
      subForms,
    });
  }, []);

  const setRequest = useCallback((request) => {
    dispatch({
      type: FORM_ACTIONS.SET_REQUEST,
      request,
    });
  }, []);

  const setFilledFormData = useCallback((filledForm) => {
    dispatch({
      type: FORM_ACTIONS.SET_FILLED_FORM,
      filledForm,
    });
  }, []);

  const setActiveSubFormTab = useCallback((activeSubFormTab) => {
    dispatch({
      type: FORM_ACTIONS.SET_ACTIVE_SUB_FORM_TAB,
      activeSubFormTab,
    });
  }, []);

  const clearFieldError = useCallback(
    (subformId, fieldName) => {
      const currentErrors = state.errors.formSections;

      if (currentErrors?.subforms?.[subformId]?.fields?.[fieldName]) {
        const updatedErrors = {
          ...currentErrors,
          subforms: {
            ...currentErrors.subforms,
            [subformId]: {
              ...currentErrors.subforms[subformId],
              fields: {
                ...currentErrors.subforms[subformId].fields,
              },
            },
          },
        };

        delete updatedErrors.subforms[subformId].fields[fieldName];

        const remainingFieldErrors = Object.keys(
          updatedErrors.subforms[subformId].fields
        );
        updatedErrors.subforms[subformId].hasErrors =
          remainingFieldErrors.length > 0;

        if (!updatedErrors.subforms[subformId].hasErrors) {
          delete updatedErrors.subforms[subformId];
        }

        const hasAnyErrors = Object.keys(updatedErrors.subforms).length > 0;
        updatedErrors.hasErrors = hasAnyErrors;

        if (hasAnyErrors) {
          const errorSummary = [];
          Object.keys(updatedErrors.subforms).forEach((subformId) => {
            const subform = updatedErrors.subforms[subformId];
            const fieldCount = Object.keys(subform.fields).length;
            errorSummary.push(
              `${fieldCount} field${fieldCount > 1 ? "s" : ""} in ${
                subform.name
              }`
            );
          });
          updatedErrors.general = `Missing required fields: ${errorSummary.join(
            ", "
          )}`;
        } else {
          updatedErrors.general = null;
        }

        setFormErrors("formSections", updatedErrors);
      }
    },
    [state.errors.formSections, setFormErrors]
  );

  const addFieldError = useCallback(
    (subformId, fieldName, fieldError) => {
      const currentErrors = state.errors.formSections || {
        subforms: {},
        hasErrors: false,
        general: null,
      };

      const subForm = state.subForms.find((sf) => sf.id === subformId);
      const subformName = subForm ? subForm.name : subformId;

      const updatedErrors = {
        ...currentErrors,
        subforms: {
          ...currentErrors.subforms,
          [subformId]: {
            name: subformName,
            hasErrors: true,
            fields: {
              ...(currentErrors.subforms?.[subformId]?.fields || {}),
              [fieldName]: fieldError,
            },
          },
        },
        hasErrors: true,
      };

      const errorSummary = [];
      Object.keys(updatedErrors.subforms).forEach((subformId) => {
        const subform = updatedErrors.subforms[subformId];
        const fieldCount = Object.keys(subform.fields).length;
        errorSummary.push(
          `${fieldCount} field${fieldCount > 1 ? "s" : ""} in ${subform.name}`
        );
      });
      updatedErrors.general = `Missing required fields: ${errorSummary.join(
        ", "
      )}`;

      setFormErrors("formSections", updatedErrors);
    },
    [state.errors.formSections, state.subForms, setFormErrors]
  );

  const clearGenericFieldError = useCallback(
    (formType, fieldName) => {
      const currentErrors = state.errors[formType];

      if (currentErrors?.fields?.[fieldName]) {
        const updatedErrors = {
          ...currentErrors,
          fields: {
            ...currentErrors.fields,
          },
        };

        delete updatedErrors.fields[fieldName];

        const remainingFieldErrors = Object.keys(updatedErrors.fields);
        updatedErrors.hasErrors = remainingFieldErrors.length > 0;

        if (updatedErrors.hasErrors) {
          const errorMessages = remainingFieldErrors.map(
            (fname) => updatedErrors.fields[fname].label
          );
          updatedErrors.general = `Missing or invalid fields: ${errorMessages.join(
            ", "
          )}`;
        } else {
          updatedErrors.general = null;
        }

        setFormErrors(formType, updatedErrors);
      }
    },
    [state.errors, setFormErrors]
  );

  const addGenericFieldError = useCallback(
    (formType, fieldName, fieldError) => {
      const currentErrors = state.errors[formType] || {
        fields: {},
        hasErrors: false,
        general: null,
      };

      const updatedErrors = {
        ...currentErrors,
        fields: {
          ...currentErrors.fields,
          [fieldName]: fieldError,
        },
        hasErrors: true,
      };

      const errorMessages = Object.keys(updatedErrors.fields).map(
        (fname) => updatedErrors.fields[fname].label
      );
      updatedErrors.general = `Missing or invalid fields: ${errorMessages.join(
        ", "
      )}`;

      setFormErrors(formType, updatedErrors);
    },
    [state.errors, setFormErrors]
  );

  const syncAllTabsData = useCallback(
    async (subForms, currentActiveTab, setActiveTab) => {
      if (!subForms || subForms.length === 0) {
        return {};
      }

      const allFormData = {};
      const originalTab = currentActiveTab;

      for (const subForm of subForms) {
        if (setActiveTab && typeof setActiveTab === "function") {
          setActiveTab(subForm.id);
          await new Promise((resolve) =>
            setTimeout(resolve, FORM_TIMING.HYDRATION_DELAY_MS)
          );
        }

        const formData = getFormSectionsData();

        if (formData && formData[subForm.id]) {
          allFormData[subForm.id] = formData[subForm.id];
        } else {
          allFormData[subForm.id] = {};
        }
      }

      if (setActiveTab && typeof setActiveTab === "function" && originalTab) {
        setActiveTab(originalTab);
        await new Promise((resolve) =>
          setTimeout(resolve, FORM_TIMING.SYNC_DELAY_MS)
        );
      }

      return allFormData;
    },
    [getFormSectionsData]
  );

  const syncFormSectionsData = useCallback(
    async (subForms = null, activeTab = null, setActiveTab = null) => {
      if (subForms && Array.isArray(subForms) && subForms.length > 1) {
        const allFormData = await syncAllTabsData(
          subForms,
          activeTab,
          setActiveTab
        );

        if (Object.keys(allFormData).length > 0) {
          const mergedData = {
            ...state.formSections,
            ...allFormData,
          };

          setFormData("formSections", mergedData);

          return mergedData;
        }
      }

      const queries = [
        'input[name^="text-"], textarea[name^="textarea-"]',
        'input[type="text"], textarea',
        ".form-control",
        "input, textarea, select",
        "[data-subform] input, [data-subform] textarea",
      ];

      let allInputs = [];
      queries.forEach((query, index) => {
        const inputs = document.querySelectorAll(query);
        allInputs = [...allInputs, ...Array.from(inputs)];
      });

      const uniqueInputs = [...new Set(allInputs)];

      await new Promise((resolve) =>
        setTimeout(resolve, FORM_TIMING.SYNC_DELAY_MS)
      );

      const jqueryData = getFormSectionsData();

      const domHasData = uniqueInputs.some(
        (input) => input.value && input.value.trim() !== ""
      );
      const jqueryHasData = Object.values(jqueryData).some((formData) =>
        Object.values(formData || {}).some(
          (value) => value && value.trim() !== ""
        )
      );

      if (domHasData && !jqueryHasData) {
        return state.formSections;
      }

      if (Object.keys(jqueryData).length > 0) {
        const mergedData = {
          ...state.formSections,
          ...jqueryData,
        };

        setFormData("formSections", mergedData);

        return mergedData;
      } else {
        return state.formSections;
      }
    },
    [getFormSectionsData, setFormData, state.formSections, syncAllTabsData]
  );

  const validateDrugPricingForm = useCallback((data) => {
    const errors = {
      fields: {},
      hasErrors: false,
      general: null,
    };

    let totalErrorCount = 0;
    const errorMessages = [];

    // only drug_name_brand is required
    const requiredFields = [
      { name: "drug_name_brand", label: "Drug Brand Name" },
    ];

    requiredFields.forEach((field) => {
      if (!data[field.name] || data[field.name] === "") {
        errors.fields[field.name] = {
          label: field.label,
          error: `${field.label} is required`,
          required: true,
        };
        totalErrorCount++;
        errorMessages.push(field.label);
      }
    });

    // validate email format (optional field, only validate if provided)
    if (
      data.contact_email &&
      data.contact_email !== "" &&
      !VALIDATION.EMAIL_REGEX.test(data.contact_email)
    ) {
      errors.fields.contact_email = {
        label: "Contact Email",
        error: "Invalid email format",
        required: false,
      };
      if (!errorMessages.includes("Contact Email")) {
        totalErrorCount++;
        errorMessages.push("Contact Email");
      }
    }

    // validate price is a number (optional field, only validate if provided)
    if (
      data.proposed_price_local &&
      data.proposed_price_local !== "" &&
      isNaN(parseFloat(data.proposed_price_local))
    ) {
      errors.fields.proposed_price_local = {
        label: "Proposed Price (Local)",
        error: "Price must be a valid number",
        required: false,
      };
      if (!errorMessages.includes("Proposed Price (Local)")) {
        totalErrorCount++;
        errorMessages.push("Proposed Price (Local)");
      }
    }

    if (
      data.reference_country_prices &&
      Array.isArray(data.reference_country_prices)
    ) {
      data.reference_country_prices.forEach((item, index) => {
        const hasCountry = item.country && item.country.trim() !== "";
        const hasPrice =
          item.price !== null && item.price !== undefined && item.price !== "";
        const hasCurrency = item.currency && item.currency.trim() !== "";

        const filledCount = [hasCountry, hasPrice, hasCurrency].filter(
          Boolean
        ).length;

        if (filledCount > 0 && filledCount < 3) {
          if (!hasCountry && (hasPrice || hasCurrency)) {
            errors.fields[`reference_country_prices_${index}_country`] = {
              label: `Country (Row ${index + 1})`,
              error: "Country is required",
              required: true,
            };
            totalErrorCount++;
            errorMessages.push(`Country (Row ${index + 1})`);
          }

          if (!hasPrice && (hasCountry || hasCurrency)) {
            errors.fields[`reference_country_prices_${index}_price`] = {
              label: `Price (Row ${index + 1})`,
              error: "Price is required",
              required: true,
            };
            totalErrorCount++;
            errorMessages.push(`Price (Row ${index + 1})`);
          }

          if (!hasCurrency && (hasCountry || hasPrice)) {
            errors.fields[`reference_country_prices_${index}_currency`] = {
              label: `Currency (Row ${index + 1})`,
              error: "Currency is required",
              required: true,
            };
            totalErrorCount++;
            errorMessages.push(`Currency (Row ${index + 1})`);
          }
        }
      });
    }

    errors.hasErrors = totalErrorCount > 0;

    if (errors.hasErrors) {
      errors.general = `Missing or invalid fields: ${errorMessages.join(", ")}`;
    }

    return { errors, isValid: !errors.hasErrors };
  }, []);

  const validateEctdUpload = useCallback(
    (data, originalData = null) => {
      const errors = {
        fields: {},
        hasErrors: false,
        general: null,
      };

      let totalErrorCount = 0;
      const errorMessages = [];

      // Validate file upload
      const hasFile = data.ectd_file && data.ectd_file.length > 0;
      if (!hasFile) {
        errors.fields.ectd_file = {
          label: "eCTD Document",
          error: "Please upload your eCTD document to continue",
          required: true,
        };
        totalErrorCount++;
        errorMessages.push("eCTD Document");
      }

      // Validate sequence number
      const hasSequenceNumber =
        data.sequence_number && data.sequence_number !== "";
      if (!hasSequenceNumber) {
        errors.fields.sequence_number = {
          label: "Sequence Number",
          error: "Sequence Number is required",
          required: true,
        };
        totalErrorCount++;
        errorMessages.push("Sequence Number");
      }

      // Validate submission ID
      // const hasSubmissionId = data.submission_id && data.submission_id !== "";
      // if (!hasSubmissionId) {
      //   errors.fields.submission_id = {
      //     label: "Submission ID",
      //     error: "Submission ID is required",
      //     required: true
      //   };
      //   totalErrorCount++;
      //   errorMessages.push("Submission ID");
      // }

      // Check if file changed and sequence number hasn't (only file changes require new sequence number)
      if (originalData && isSendbackMode) {
        const sequenceNumberUnchanged =
          originalData.sequence_number &&
          data.sequence_number === originalData.sequence_number;

        const fileChanged =
          originalData.ectd_file &&
          data.ectd_file &&
          data.ectd_file.length > 0 &&
          originalData.ectd_file.length > 0 &&
          originalData.ectd_file[0]?.id !== data.ectd_file[0]?.id;

        if (fileChanged && sequenceNumberUnchanged) {
          errors.fields.sequence_number = {
            label: "Sequence Number",
            error:
              "Sequence Number must be changed when uploading a new eCTD file",
            required: true,
          };
          if (!errorMessages.includes("Sequence Number")) {
            totalErrorCount++;
            errorMessages.push("Sequence Number");
          }
        }
      }

      errors.hasErrors = totalErrorCount > 0;

      if (errors.hasErrors) {
        const fieldWord = errorMessages.length > 1 ? "fields" : "field";
        errors.general = `Please fill in the following required ${fieldWord}: ${errorMessages.join(
          ", "
        )}`;
      }

      return { errors, isValid: !errors.hasErrors };
    },
    [isSendbackMode]
  );

  const validateProductDetails = useCallback((data) => {
    const errors = {
      fields: {},
      hasErrors: false,
      general: null,
    };

    let totalErrorCount = 0;
    const errorMessages = [];

    // only proposed_trade_name_in_english is required
    const requiredFields = [
      {
        name: "proposed_trade_name_in_english",
        label: "proposed trade name (english)",
      },
    ];

    requiredFields.forEach((field) => {
      if (!data[field.name] || data[field.name] === "") {
        errors.fields[field.name] = {
          label: field.label,
          error: `${field.label} is required`,
          required: true,
        };
        totalErrorCount++;
        errorMessages.push(field.label);
      }
    });

    // validate atc_code is a number (optional field, only validate if provided)
    if (
      data.atc_code &&
      data.atc_code !== "" &&
      isNaN(parseFloat(data.atc_code))
    ) {
      errors.fields.atc_code = {
        label: "atc code",
        error: "atc code must be a valid number",
        required: false,
      };
      if (!errorMessages.includes("atc code")) {
        totalErrorCount++;
        errorMessages.push("atc code");
      }
    }

    // validate concentration is a number (optional field, only validate if provided)
    if (
      data.concentration &&
      data.concentration !== "" &&
      isNaN(parseFloat(data.concentration))
    ) {
      errors.fields.concentration = {
        label: "concentration",
        error: "concentration must be a valid number",
        required: false,
      };
      if (!errorMessages.includes("concentration")) {
        totalErrorCount++;
        errorMessages.push("concentration");
      }
    }

    errors.hasErrors = totalErrorCount > 0;

    if (errors.hasErrors) {
      errors.general = `missing or invalid fields: ${errorMessages.join(", ")}`;
    }

    return { errors, isValid: !errors.hasErrors };
  }, []);

  const validateManufacturersSite = useCallback((data) => {
    const errors = {
      fields: {},
      hasErrors: false,
      general: null,
    };

    let totalErrorCount = 0;
    const errorMessages = [];

    // only manufacturer_name is required
    const requiredFields = [
      { name: "manufacturer_name", label: "manufacturer name" },
    ];

    requiredFields.forEach((field) => {
      if (!data[field.name] || data[field.name] === "") {
        errors.fields[field.name] = {
          label: field.label,
          error: `${field.label} is required`,
          required: true,
        };
        totalErrorCount++;
        errorMessages.push(field.label);
      }
    });

    // validate email format (optional field, only validate if provided)
    if (
      data.email &&
      data.email !== "" &&
      !VALIDATION.EMAIL_REGEX.test(data.email)
    ) {
      errors.fields.email = {
        label: "email",
        error: "invalid email format",
        required: false,
      };
      if (!errorMessages.includes("email")) {
        totalErrorCount++;
        errorMessages.push("email");
      }
    }

    errors.hasErrors = totalErrorCount > 0;

    if (errors.hasErrors) {
      errors.general = `missing or invalid fields: ${errorMessages.join(", ")}`;
    }

    return { errors, isValid: !errors.hasErrors };
  }, []);

  // ref to track validation timeouts for debouncing
  const validationTimeoutRef = useRef({});

  const setFormDataWithValidation = useCallback(
    (formType, data) => {
      // set form data immediately
      dispatch({
        type: FORM_ACTIONS.SET_FORM_DATA,
        formType,
        data,
      });

      // clear any existing validation timeout for this form type
      if (validationTimeoutRef.current[formType]) {
        clearTimeout(validationTimeoutRef.current[formType]);
      }

      // debounce validation to reduce re-renders - batch errors and validity together
      validationTimeoutRef.current[formType] = setTimeout(() => {
        let validationResult = null;

        switch (formType) {
          case "drugPricing":
            validationResult = validateDrugPricingForm(data);
            break;
          case "ectdUpload":
            validationResult = validateEctdUpload(
              data,
              state.originalEctdUpload
            );
            break;
          case "productDetails":
            validationResult = validateProductDetails(data);
            break;
          case "manufacturersSite":
            validationResult = validateManufacturersSite(data);
            break;
          default:
            return;
        }

        if (validationResult) {
          // batch both updates into single setTimeout to reduce re-renders
          dispatch({
            type: FORM_ACTIONS.SET_FORM_ERRORS,
            formType,
            errors: validationResult.errors,
          });

          dispatch({
            type: FORM_ACTIONS.SET_FORM_VALIDITY,
            formType,
            valid: validationResult.isValid,
          });
        }

        delete validationTimeoutRef.current[formType];
      }, 100); // increased from 0ms to 100ms to batch rapid changes
    },
    [
      validateDrugPricingForm,
      validateEctdUpload,
      validateProductDetails,
      validateManufacturersSite,
      state.originalEctdUpload,
    ]
  );

  const validateFormSections = useCallback((data, subForms) => {
    const errors = {
      subforms: {},
      hasErrors: false,
      general: null,
    };

    let totalErrorCount = 0;
    const errorSummary = [];

    for (const subForm of subForms || []) {
      const formData = data[subForm.id] || {};
      const subformErrors = {
        name: subForm.name,
        hasErrors: false,
        fields: {},
      };

      let formFields = [];

      try {
        formFields = JSON.parse(subForm.json_object);
      } catch (e) {
        continue;
      }

      if (Array.isArray(formFields)) {
        for (const field of formFields) {
          if (field && field.required) {
            const fieldValue = formData[field.name];
            const isEmpty = !fieldValue || fieldValue === "";

            if (isEmpty) {
              const cleanedLabel = cleanLabel(field.label || field.name);
              subformErrors.hasErrors = true;
              subformErrors.fields[field.name] = {
                label: cleanedLabel,
                error: `${cleanedLabel} is required`,
                required: true,
              };
              totalErrorCount++;
            }
          }
        }
      }

      if (subformErrors.hasErrors) {
        errors.subforms[subForm.id] = subformErrors;
        const fieldCount = Object.keys(subformErrors.fields).length;
        errorSummary.push(
          `${fieldCount} field${fieldCount > 1 ? "s" : ""} in ${subForm.name}`
        );
      }
    }

    errors.hasErrors = totalErrorCount > 0;

    if (errors.hasErrors) {
      errors.general = `Missing required fields: ${errorSummary.join(", ")}`;
    }

    return { errors, isValid: !errors.hasErrors };
  }, []);

  const submitAllForms = useCallback(async () => {
    const subForms = state.subForms;

    dispatch({
      type: FORM_ACTIONS.SET_SUBMISSION_STATE,
      state: { isSubmitting: true, error: null, success: false },
    });

    try {
      const formSectionsData = trimStringValues(state.formSections);
      const drugPricingData = trimStringValues(state.drugPricing);
      const ectdUploadData = trimStringValues(state.ectdUpload);
      const productDetailsData = trimStringValues(state.productDetails);
      const manufacturersSiteDataRaw = trimStringValues(
        state.manufacturersSite
      );

      // clean up __OTHER__ value from selected_manufacturer before submission
      const manufacturersSiteData = {
        ...manufacturersSiteDataRaw,
        selected_manufacturer:
          manufacturersSiteDataRaw?.selected_manufacturer === "__OTHER__"
            ? ""
            : manufacturersSiteDataRaw?.selected_manufacturer,
      };

      const formSectionsValidation = validateFormSections(
        formSectionsData,
        subForms
      );
      const drugPricingValidation = validateDrugPricingForm(drugPricingData);
      const ectdValidation = validateEctdUpload(
        ectdUploadData,
        state.originalEctdUpload
      );

      // validate productDetails if enabled
      let productDetailsValidation = { isValid: true, errors: {} };
      if (
        state.hasProductDetails &&
        productDetailsData &&
        Object.keys(productDetailsData).length > 0
      ) {
        productDetailsValidation = validateProductDetails(productDetailsData);
        setFormValidity("productDetails", productDetailsValidation.isValid);
        setFormErrors("productDetails", productDetailsValidation.errors);
      }

      // validate manufacturersSite if enabled
      let manufacturersSiteValidation = { isValid: true, errors: {} };
      if (
        state.hasManufacturersSite &&
        manufacturersSiteData &&
        Object.keys(manufacturersSiteData).length > 0
      ) {
        manufacturersSiteValidation = validateManufacturersSite(
          manufacturersSiteData
        );
        setFormValidity(
          "manufacturersSite",
          manufacturersSiteValidation.isValid
        );
        setFormErrors("manufacturersSite", manufacturersSiteValidation.errors);
      }

      setFormValidity("formSections", formSectionsValidation.isValid);
      setFormValidity("drugPricing", drugPricingValidation.isValid);
      setFormValidity("ectdUpload", ectdValidation.isValid);

      setFormErrors("formSections", formSectionsValidation.errors);
      setFormErrors("drugPricing", drugPricingValidation.errors);
      setFormErrors("ectdUpload", ectdValidation.errors);

      const allValid =
        formSectionsValidation.isValid &&
        drugPricingValidation.isValid &&
        ectdValidation.isValid &&
        productDetailsValidation.isValid &&
        manufacturersSiteValidation.isValid;

      if (!allValid) {
        throw new Error("Please fix validation errors before submitting");
      }

      const currentStageId = state.request?.Stages?.[0]?.id || initialStageId;

      const requestBody = {
        stage_id: currentStageId,
        wf_version_id: workflowVersionId,
        study_name: studyName?.trim() || studyName,
        request_progress_id: null,
        formSections: JSON.stringify(formSectionsData),
        drugPricing: {
          ...drugPricingData,
          price_certificate_file_id: Array.isArray(
            drugPricingData.price_certificate_file_id
          )
            ? drugPricingData.price_certificate_file_id[0]?.id || null
            : drugPricingData.price_certificate_file_id || null,
          gmp_certificate_file_id: Array.isArray(
            drugPricingData.gmp_certificate_file_id
          )
            ? drugPricingData.gmp_certificate_file_id[0]?.id || null
            : drugPricingData.gmp_certificate_file_id || null,
          market_authorization_certificate_file_id: Array.isArray(
            drugPricingData.market_authorization_certificate_file_id
          )
            ? drugPricingData.market_authorization_certificate_file_id[0]?.id ||
              null
            : drugPricingData.market_authorization_certificate_file_id || null,
          packaging_samples_file_id: Array.isArray(
            drugPricingData.packaging_samples_file_id
          )
            ? drugPricingData.packaging_samples_file_id[0]?.id || null
            : drugPricingData.packaging_samples_file_id || null,
          pricing_history_file_id: Array.isArray(
            drugPricingData.pricing_history_file_id
          )
            ? drugPricingData.pricing_history_file_id[0]?.id || null
            : drugPricingData.pricing_history_file_id || null,
        },
        ectdUpload: {
          ectd_file_id: ectdUploadData.ectd_file?.[0]?.id || null,
          sequence_number: ectdUploadData.sequence_number || "",
          submission_id: ectdUploadData.submission_id || "",
          product_name: ectdUploadData.product_name || "",
          submission_type: ectdUploadData.submission_type || "",
          biologic_product_type: ectdUploadData.biologic_product_type || "",
          clinical: ectdUploadData.clinical || false,
          cmc_quality: ectdUploadData.cmc_quality || false,
          non_clinical_toxicology:
            ectdUploadData.non_clinical_toxicology || false,
          clinical_pharmacology: ectdUploadData.clinical_pharmacology || false,
          biostatistics: ectdUploadData.biostatistics || false,
          device_engineering: ectdUploadData.device_engineering || false,
          labeling: ectdUploadData.labeling || false,
          proposed_indication: ectdUploadData.proposed_indication || [],
          review_pathway: ectdUploadData.review_pathway || "",
          fast_track: ectdUploadData.fast_track || false,
          breakthrough_therapy: ectdUploadData.breakthrough_therapy || false,
          rmat: ectdUploadData.rmat || false,
          accelerated_approval: ectdUploadData.accelerated_approval || false,
          basis_of_clinical_efficacy:
            ectdUploadData.basis_of_clinical_efficacy || "",
          dosage_form_route: ectdUploadData.dosage_form_route || "",
        },
        productDetails: state.hasProductDetails
          ? productDetailsData
          : undefined,
        manufacturersSite: state.hasManufacturersSite
          ? manufacturersSiteData
          : undefined,
        additionalDocuments:
          state.additionalDocuments.length > 0
            ? state.additionalDocuments.map((doc) => ({
                comment: doc.comment,
                fileId: doc.additional_file?.id || doc.fileId || null,
              }))
            : undefined,
      };

      if (isSendbackMode) {
        try {
          const filledFormId = state.filledForm?.id;
          if (!filledFormId) {
            throw new Error("No filled form ID found for update");
          }

          const updateRequestBody = {
            formSections: JSON.stringify(formSectionsData),
            study_name: studyName?.trim() || studyName,
            drugPricing: {
              ...drugPricingData,
              price_certificate_file_id: Array.isArray(
                drugPricingData.price_certificate_file_id
              )
                ? drugPricingData.price_certificate_file_id[0]?.id || null
                : drugPricingData.price_certificate_file_id || null,
              gmp_certificate_file_id: Array.isArray(
                drugPricingData.gmp_certificate_file_id
              )
                ? drugPricingData.gmp_certificate_file_id[0]?.id || null
                : drugPricingData.gmp_certificate_file_id || null,
              market_authorization_certificate_file_id: Array.isArray(
                drugPricingData.market_authorization_certificate_file_id
              )
                ? drugPricingData.market_authorization_certificate_file_id[0]
                    ?.id || null
                : drugPricingData.market_authorization_certificate_file_id ||
                  null,
              packaging_samples_file_id: Array.isArray(
                drugPricingData.packaging_samples_file_id
              )
                ? drugPricingData.packaging_samples_file_id[0]?.id || null
                : drugPricingData.packaging_samples_file_id || null,
              pricing_history_file_id: Array.isArray(
                drugPricingData.pricing_history_file_id
              )
                ? drugPricingData.pricing_history_file_id[0]?.id || null
                : drugPricingData.pricing_history_file_id || null,
            },
            ectdUpload: {
              ectd_file_id: ectdUploadData.ectd_file?.[0]?.id || null,
              sequence_number: ectdUploadData.sequence_number || "",
              submission_id: ectdUploadData.submission_id || "",
              product_name: ectdUploadData.product_name || "",
              submission_type: ectdUploadData.submission_type || "",
              biologic_product_type: ectdUploadData.biologic_product_type || "",
              clinical: ectdUploadData.clinical || false,
              cmc_quality: ectdUploadData.cmc_quality || false,
              non_clinical_toxicology:
                ectdUploadData.non_clinical_toxicology || false,
              clinical_pharmacology:
                ectdUploadData.clinical_pharmacology || false,
              biostatistics: ectdUploadData.biostatistics || false,
              device_engineering: ectdUploadData.device_engineering || false,
              labeling: ectdUploadData.labeling || false,
              proposed_indication: ectdUploadData.proposed_indication || [],
              review_pathway: ectdUploadData.review_pathway || "",
              fast_track: ectdUploadData.fast_track || false,
              breakthrough_therapy:
                ectdUploadData.breakthrough_therapy || false,
              rmat: ectdUploadData.rmat || false,
              accelerated_approval:
                ectdUploadData.accelerated_approval || false,
              basis_of_clinical_efficacy:
                ectdUploadData.basis_of_clinical_efficacy || "",
              dosage_form_route: ectdUploadData.dosage_form_route || "",
            },
            productDetails: state.hasProductDetails
              ? productDetailsData
              : undefined,
            manufacturersSite: state.hasManufacturersSite
              ? manufacturersSiteData
              : undefined,
          };

          const response = await axiosInstance.put(
            `/api/endUserFilledForms/${filledFormId}`,
            updateRequestBody,
            {
              withCredentials: true,
              suppressErrorToast: true, // Handle error toast manually for better UX
            }
          );

          dispatch({
            type: FORM_ACTIONS.SET_SUBMISSION_STATE,
            state: {
              isSubmitting: false,
              success: true,
              error: null,
              data: {
                mode: "sendback_update",
                response: response.data,
                updateRequestBody,
                filledFormId,
              },
            },
          });

          toast.success(`"${requestBody.study_name}" updated successfully!`, {
            position: "top-center",
            autoClose: 5000,
          });

          localStorage.removeItem(storageKey);

          return true;
        } catch (error) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to update form";

          dispatch({
            type: FORM_ACTIONS.SET_SUBMISSION_STATE,
            state: {
              isSubmitting: false,
              success: false,
              error: errorMessage,
              data: null,
            },
          });

          toast.error(errorMessage, {
            position: "top-center",
            autoClose: 8000,
          });

          return false;
        }
      }

      const mainFormResponse = await axiosInstance.post(
        "/api/endUserFilledForms",
        requestBody,
        {
          suppressErrorToast: true, // Handle error toast manually for better UX
        }
      );

      dispatch({
        type: FORM_ACTIONS.SET_SUBMISSION_STATE,
        state: {
          isSubmitting: false,
          success: true,
          error: null,
          data: {
            mode: "normal_submit",
            endUserFilledForm: mainFormResponse.data,
            requestBody: requestBody,
          },
        },
      });

      toast.success("All forms submitted successfully!", {
        position: "top-center",
      });

      // Remove draft from localStorage if it was loaded from a draft
      if (draftId) {
        localStorage.removeItem(draftId);
      }
      localStorage.removeItem(storageKey);

      // Return the response data which contains request_id
      return mainFormResponse.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Submission failed";

      dispatch({
        type: FORM_ACTIONS.SET_SUBMISSION_STATE,
        state: {
          isSubmitting: false,
          success: false,
          error: errorMessage,
        },
      });

      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 8000,
      });

      return false;
    }
  }, [
    state.drugPricing,
    state.ectdUpload,
    state.originalEctdUpload,
    state.formSections,
    state.subForms,
    state.request,
    state.filledForm,
    state.hasProductDetails,
    state.productDetails,
    state.hasManufacturersSite,
    state.manufacturersSite,
    state.additionalDocuments,
    validateFormSections,
    validateDrugPricingForm,
    validateEctdUpload,
    validateProductDetails,
    validateManufacturersSite,
    setFormValidity,
    setFormErrors,
    initialStageId,
    workflowVersionId,
    studyName,
    storageKey,
    isSendbackMode,
    draftId,
  ]);

  const validateForms = useCallback(
    async (formsTypes = []) => {
      for (const formType of formsTypes) {
        switch (formType) {
          case FORM_TYPES.FORM_SECTIONS:
            const formSectionsValidation = validateFormSections(
              state.formSections,
              state.subForms
            );
            setFormValidity("formSections", formSectionsValidation.isValid);
            setFormErrors("formSections", formSectionsValidation.errors);
            break;

          case FORM_TYPES.DRUG_PRICING:
            const drugPricingValidation = validateDrugPricingForm(
              state.drugPricing
            );
            setFormValidity("drugPricing", drugPricingValidation.isValid);
            setFormErrors("drugPricing", drugPricingValidation.errors);
            break;

          case FORM_TYPES.ECTD_UPLOAD:
            const ectdValidation = validateEctdUpload(
              state.ectdUpload,
              state.originalEctdUpload
            );
            setFormValidity("ectdUpload", ectdValidation.isValid);
            setFormErrors("ectdUpload", ectdValidation.errors);
            break;

          case FORM_TYPES.PRODUCT_DETAILS:
            // validate productDetails if enabled
            const productDetailsData = state.productDetails;
            let productDetailsValidation = { isValid: true, errors: {} };
            if (
              state.hasProductDetails &&
              productDetailsData &&
              Object.keys(productDetailsData).length > 0
            ) {
              productDetailsValidation =
                validateProductDetails(productDetailsData);
              setFormValidity(
                "productDetails",
                productDetailsValidation.isValid
              );
              setFormErrors("productDetails", productDetailsValidation.errors);
            }
            break;

          case FORM_TYPES.MANUFACTURERS_SITE:
            // validate manufacturersSite if enabled
            const manufacturersSiteData = state.manufacturersSite;
            let manufacturersSiteValidation = { isValid: true, errors: {} };
            if (
              state.hasManufacturersSite &&
              manufacturersSiteData &&
              Object.keys(manufacturersSiteData).length > 0
            ) {
              manufacturersSiteValidation = validateManufacturersSite(
                manufacturersSiteData
              );
              setFormValidity(
                "manufacturersSite",
                manufacturersSiteValidation.isValid
              );
              setFormErrors(
                "manufacturersSite",
                manufacturersSiteValidation.errors
              );
            }
            break;

          default:
            break;
        }
      }
    },
    [
      state.drugPricing,
      state.ectdUpload,
      state.originalEctdUpload,
      state.formSections,
      state.subForms,
      state.hasProductDetails,
      state.productDetails,
      state.hasManufacturersSite,
      state.manufacturersSite,
      validateFormSections,
      validateDrugPricingForm,
      validateEctdUpload,
      validateProductDetails,
      validateManufacturersSite,
      setFormValidity,
      setFormErrors,
    ]
  );

  const resetForms = useCallback(() => {
    dispatch({ type: FORM_ACTIONS.RESET_FORMS });
    localStorage.removeItem(storageKey);
    formSectionsDataRef.current = {};
  }, [storageKey]);

  useEffect(() => {
    if (!state.isApiDataLoaded) {
      if (isSendbackMode && requestProgressId) {
        fetchSendbackDataAndInitialize(requestProgressId);
      } else if (workflowVersionId) {
        fetchWorkflowDataAndInitialize(workflowVersionId);
      }
    }
  }, [
    workflowVersionId,
    requestProgressId,
    isSendbackMode,
    state.isApiDataLoaded,
    fetchWorkflowDataAndInitialize,
    fetchSendbackDataAndInitialize,
  ]);

  useEffect(() => {
    if (
      state.isHydrationComplete &&
      state.isApiDataLoaded &&
      !state.isInitializationComplete
    ) {
      performInitializationIfNeeded(state.subForms);
    }
  }, [
    state.isHydrationComplete,
    state.isApiDataLoaded,
    state.isInitializationComplete,
    state.subForms,
    performInitializationIfNeeded,
  ]);

  useEffect(() => {
    if (state.subForms.length > 0 && !state.activeSubFormTab) {
      setActiveSubFormTab(state.subForms[0].id);
    }
  }, [state.subForms, state.activeSubFormTab, setActiveSubFormTab]);

  // ref to track if initial validation has run
  const initialValidationRunRef = useRef(false);

  // optimized validation effect - only runs on hydration, not on every state change
  useEffect(() => {
    // only run initial validation once after hydration
    if (
      state.isHydrationComplete &&
      state.hasHydratedData &&
      !initialValidationRunRef.current
    ) {
      initialValidationRunRef.current = true;

      setTimeout(() => {
        try {
          // batch all validation checks and only dispatch if changes detected
          const updates = [];

          if (validateDrugPricingForm) {
            const drugPricingValidation = validateDrugPricingForm(
              state.drugPricing
            );
            const currentDrugPricingErrors = state.errors.drugPricing;
            const currentDrugPricingValidity = state.validity.drugPricing;

            const drugPricingErrorsChanged =
              JSON.stringify(currentDrugPricingErrors) !==
              JSON.stringify(drugPricingValidation.errors);
            const drugPricingValidityChanged =
              currentDrugPricingValidity !== drugPricingValidation.isValid;

            if (drugPricingErrorsChanged || drugPricingValidityChanged) {
              if (drugPricingErrorsChanged) {
                updates.push({
                  type: FORM_ACTIONS.SET_FORM_ERRORS,
                  formType: "drugPricing",
                  errors: drugPricingValidation.errors,
                });
              }
              if (drugPricingValidityChanged) {
                updates.push({
                  type: FORM_ACTIONS.SET_FORM_VALIDITY,
                  formType: "drugPricing",
                  valid: drugPricingValidation.isValid,
                });
              }
            }
          }

          if (validateEctdUpload) {
            const ectdValidation = validateEctdUpload(
              state.ectdUpload,
              state.originalEctdUpload
            );
            const currentEctdErrors = state.errors.ectdUpload;
            const currentEctdValidity = state.validity.ectdUpload;

            const ectdErrorsChanged =
              JSON.stringify(currentEctdErrors) !==
              JSON.stringify(ectdValidation.errors);
            const ectdValidityChanged =
              currentEctdValidity !== ectdValidation.isValid;

            if (ectdErrorsChanged || ectdValidityChanged) {
              if (ectdErrorsChanged) {
                updates.push({
                  type: FORM_ACTIONS.SET_FORM_ERRORS,
                  formType: "ectdUpload",
                  errors: ectdValidation.errors,
                });
              }
              if (ectdValidityChanged) {
                updates.push({
                  type: FORM_ACTIONS.SET_FORM_VALIDITY,
                  formType: "ectdUpload",
                  valid: ectdValidation.isValid,
                });
              }
            }
          }

          if (
            state.subForms &&
            state.subForms.length > 0 &&
            validateFormSections
          ) {
            const formSectionsValidation = validateFormSections(
              state.formSections,
              state.subForms
            );
            const currentFormSectionsErrors = state.errors.formSections;
            const currentFormSectionsValidity = state.validity.formSections;

            const formSectionsErrorsChanged =
              JSON.stringify(currentFormSectionsErrors) !==
              JSON.stringify(formSectionsValidation.errors);
            const formSectionsValidityChanged =
              currentFormSectionsValidity !== formSectionsValidation.isValid;

            if (formSectionsErrorsChanged || formSectionsValidityChanged) {
              if (formSectionsErrorsChanged) {
                updates.push({
                  type: FORM_ACTIONS.SET_FORM_ERRORS,
                  formType: "formSections",
                  errors: formSectionsValidation.errors,
                });
              }
              if (formSectionsValidityChanged) {
                updates.push({
                  type: FORM_ACTIONS.SET_FORM_VALIDITY,
                  formType: "formSections",
                  valid: formSectionsValidation.isValid,
                });
              }
            }
          }

          // batch dispatch all updates
          updates.forEach((update) => dispatch(update));
        } catch (error) {
          // silent error handling
        }
      }, FORM_TIMING.SYNC_DELAY_MS);
    }
  }, [
    state.isHydrationComplete,
    state.hasHydratedData,
    state.drugPricing,
    state.ectdUpload,
    state.originalEctdUpload,
    state.formSections,
    state.subForms,
    state.errors.drugPricing,
    state.errors.ectdUpload,
    state.errors.formSections,
    state.validity.drugPricing,
    state.validity.ectdUpload,
    state.validity.formSections,
    validateDrugPricingForm,
    validateEctdUpload,
    validateFormSections,
  ]);

  useEffect(() => {
    window.clearFieldError = clearFieldError;
    window.addFieldError = addFieldError;
    window.clearGenericFieldError = clearGenericFieldError;
    window.addGenericFieldError = addGenericFieldError;

    window.getCurrentSubFormDefinition = (subformId) => {
      return state.subForms.find((sf) => sf.id === subformId);
    };

    window.getValidationFunctions = () => ({
      validateDrugPricingForm,
      validateEctdUpload,
      validateFormSections,
    });

    return () => {
      delete window.clearFieldError;
      delete window.addFieldError;
      delete window.clearGenericFieldError;
      delete window.addGenericFieldError;
      delete window.getCurrentSubFormDefinition;
      delete window.getValidationFunctions;
    };
  }, [
    clearFieldError,
    addFieldError,
    clearGenericFieldError,
    addGenericFieldError,
    state.subForms,
    validateDrugPricingForm,
    validateEctdUpload,
    validateFormSections,
  ]);

  const saveAsDraft = useCallback(
    async (
      draftName,
      subForms = null,
      activeTab = null,
      setActiveTab = null
    ) => {
      try {
        const syncedData = await syncFormSectionsData(
          subForms || state.subForms,
          activeTab,
          setActiveTab
        );

        await new Promise((resolve) =>
          setTimeout(resolve, FORM_TIMING.HYDRATION_DELAY_MS)
        );

        const timestamp = Date.now();
        const draftId = `${STORAGE.MULTI_FORM_PREFIX}newRequest_${workflowVersionId}_${timestamp}`;

        const dataToSave = {
          draftId,
          draftName:
            draftName || `Draft ${new Date(timestamp).toLocaleString("en-GB")}`,
          workflowVersionId,
          formName: state.workflowName || state.formName,
          formSections: syncedData || state.formSections,
          drugPricing: state.drugPricing,
          ectdUpload: state.ectdUpload,
          productDetails: state.productDetails,
          manufacturersSite: state.manufacturersSite,
          additionalDocuments: state.additionalDocuments,
          timestamp,
        };

        const hasMeaningfulData = Object.keys(
          dataToSave.formSections || {}
        ).some((formId) =>
          Object.values(dataToSave.formSections[formId] || {}).some(
            (val) => val && val.trim() !== ""
          )
        );

        if (
          hasMeaningfulData ||
          Object.values(dataToSave.drugPricing || {}).some(
            (val) => val && val !== ""
          ) ||
          (dataToSave.ectdUpload?.ectd_file &&
            dataToSave.ectdUpload.ectd_file.length > 0) ||
          Object.values(dataToSave.productDetails || {}).some(
            (val) => val && val !== ""
          ) ||
          Object.values(dataToSave.manufacturersSite || {}).some(
            (val) => val && val !== ""
          )
        ) {
          localStorage.setItem(draftId, JSON.stringify(dataToSave));
        }

        return { success: true, message: "Draft saved successfully!", draftId };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [
      syncFormSectionsData,
      state.formSections,
      state.drugPricing,
      state.ectdUpload,
      state.productDetails,
      state.manufacturersSite,
      state.additionalDocuments,
      state.subForms,
      state.workflowName,
      state.formName,
      workflowVersionId,
    ]
  );

  const setProductDetailsData = useCallback((data) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_DATA,
      formType: "productDetails",
      data,
    });
  }, []);

  const addAdditionalDocument = useCallback((document) => {
    const newDoc = {
      ...document,
      id:
        document.id ||
        `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      isOwner: true,
      isTemp: true,
    };
    dispatch({
      type: FORM_ACTIONS.ADD_ADDITIONAL_DOCUMENT,
      document: newDoc,
    });
    return newDoc;
  }, []);

  const updateAdditionalDocument = useCallback((documentId, updates) => {
    dispatch({
      type: FORM_ACTIONS.UPDATE_ADDITIONAL_DOCUMENT,
      documentId,
      updates,
    });
  }, []);

  const deleteAdditionalDocument = useCallback((documentId) => {
    dispatch({
      type: FORM_ACTIONS.DELETE_ADDITIONAL_DOCUMENT,
      documentId,
    });
  }, []);

  const setAdditionalDocuments = useCallback((documents) => {
    dispatch({
      type: FORM_ACTIONS.SET_ADDITIONAL_DOCUMENTS,
      documents,
    });
  }, []);

  const contextValue = {
    ...state,

    setFormData,
    setFormDataWithValidation,
    setFormErrors,
    setFormLoading,
    setFormValidity,

    setFormSectionsDataRef,
    getFormSectionsData,
    syncFormSectionsData,
    syncAllTabsData,

    validateDrugPricingForm,
    validateEctdUpload,
    validateFormSections,
    validateProductDetails,
    validateManufacturersSite,
    validateForms,

    submitAllForms,
    resetForms,
    saveAsDraft,

    clearFormErrors,
    fetchWorkflowDataAndInitialize,
    performInitializationIfNeeded,

    setSubForms,
    setRequest,
    setFilledFormData,

    setActiveSubFormTab,

    clearFieldError,
    addFieldError,
    clearGenericFieldError,
    addGenericFieldError,

    setProductDetailsData,

    addAdditionalDocument,
    updateAdditionalDocument,
    deleteAdditionalDocument,
    setAdditionalDocuments,

    requestId,
    requestProgressId,
    isSendbackMode,
  };

  return (
    <MultiFormContext.Provider value={contextValue}>
      {children}
    </MultiFormContext.Provider>
  );
};

export const useMultiForm = () => {
  const context = useContext(MultiFormContext);
  if (!context) {
    throw new Error("useMultiForm must be used within a MultiFormProvider");
  }
  return context;
};

export default MultiFormContext;
